import type { PlatformShopAuthWayType } from 'components/AuthShop/components/PlatformShopAuthWay';
import { StorehouseType } from 'components/AuthShop/components/ShopeeCNSCGuide';
import { ShopeeAuthWay } from 'components/AuthShop/components/ShopeeShopAuthWay';
import chatttSDK from 'services/chattt';
import {
  storeHomeClient,
  ImResponse,
  loginClient,
  resolve,
  storeHomeV2Client,
  doorClient,
  storeHomeNVClient,
  loginClientV2,
  getHeaderLang,
} from '.';
import { AuthShopOpenMode } from './flagr';
import { TokopediaStoreType } from './tokopedia';
import { chatttSDKChannel } from 'services/chattt/sdk';
import { SearchStoreListScene } from './store';

export const sendSMSCaptcha = (
  body:
    | {
        phoneNumber: string;
        purpose: 'verify' | 'register';
      }
    | {
        email: string;
        lang: General.SystemLanguage;
        purpose: 'register';
      },
) => {
  return loginClient.post<ImResponse<{ sendCodeTimestamp: number }>>('/admin/verification/codes', {
    ...body,
    lang: (body as any).lang ? getHeaderLang((body as any).lang) : undefined,
  });
};

export const createVerificationToken = (phoneNumber: string, captcha: string) => {
  return loginClient.post<ImResponse<{ verificationToken: string }>>('/admin/verification/tokens', {
    phoneNumber,
    code: captcha,
  });
};

export const createOrg = (body: {
  verificationToken: string;
  name: string;
  userName: string;
  email: string;
  password: string;
  channel?: string;
}) => {
  return loginClient.post<
    ImResponse<{
      userId: number;
      orgId: number;
    }>
  >('admin/orgs', {
    source: chatttSDK ? undefined : 'web_config',
    channel: chatttSDKChannel,
    ...body,
  });
};

interface CommonCreateData {
  channel?: string;
  inviteCode?: string;
}

interface PhoneCreateData extends CommonCreateData {
  smsCode: string;
  phone: string;
}

interface EmailCreateData extends CommonCreateData {
  email: string;
  password: string;
}

export const createOrgV2 = (body: (PhoneCreateData | EmailCreateData) & { timeZone: number }) => {
  return loginClientV2.post<
    ImResponse<{
      token: string;
    }>
  >('/admin/orgs', {
    source: chatttSDK ? undefined : 'web_config',
    channel: chatttSDKChannel,
    ...body,
  });
};

// admin/org/done_auth_tutorial
// 获取 org 是否完成新手引导
export const getIsOrgDoneAuthTutorial = () => {
  return storeHomeClient.get<ImResponse<{ isTutorialDone: boolean }>>(
    'admin/org/done_auth_tutorial',
  );
};

// admin/org/done_auth_tutorial
// 设置 org 是否完成新手引导
export const setIsOrgDoneAuthTutorial = () => {
  return storeHomeClient.put<ImResponse>('admin/org/done_auth_tutorial', {});
};

export type RegionCountsMap = { [K in General.Region as `${K}Cnt`]: number };

export interface RegionsStoreCount {
  shopeeCountData: RegionCountsMap;
  lazadaCountData: RegionCountsMap;
  tokopediaCountData?: RegionCountsMap;
  tiktokCountData: RegionCountsMap;
  aliExpressCountData?: RegionCountsMap;
  tiktokUsCountData?: RegionCountsMap;
  mercadoCountData?: RegionCountsMap;
  kwaiCountData?: RegionCountsMap;
  facebookCountData?: RegionCountsMap;
  jolectCountData?: RegionCountsMap;
}

export const getRegionsStoreCount = (permId?: General.PermissionId, signal?: AbortSignal) => {
  return storeHomeV2Client.get<ImResponse<RegionsStoreCount>>('admin/regions/store_count', {
    params: { permId },
    signal,
  });
};

export interface TokenDate {
  year: number;
  month: number;
  day: number;
}

export interface TokenTime {
  hour: number;
  minute: number;
  second: number;
  nano: number;
}

export interface AdminStore {
  id: number;
  orgId: number;
  storeName: string;
  externalId: string;
  alias: string;
  region: General.UpperCaseRegion;
  externalType: IM_API.Admin.ExternalType;
  contractStartTime: number;
  contractEndTime: number;
  authTimestamp: number;
  expireTimeStamp: number;
  domain: string;
  productId: number;
  chatAuthStatus: IM_API.Admin.AuthStatus;
  erpAuthStatus: IM_API.Admin.AuthStatus;
  authExceptionInfo?: {
    missingCsatPermission: boolean;
    missingProductReviewPermission: boolean;
  };
  authStatus: IM_API.Admin.AuthStatus;
  authType: IM_API.Admin.AuthType;
  storeType: IM_API.Admin.StoreType;
  isStoreInitFinish: boolean;
  chatShopeeToken?: {
    id: number;
    partnerId: number;
    partnerType: string;
    orgId: number;
    storeId: number;
    externalType: string;
    externalId: string;
    storeType: string;
    refreshToken: string;
    accessToken: string;
    refreshTokenCreateTime: {
      date: TokenDate;
      time: TokenTime;
    };
    accessTokenCreateTime: {
      date: TokenDate;
      time: TokenTime;
    };
  };
  isAccountUpdate: boolean;
  isSip: boolean;
  isPrimarySip: boolean;
  authAccount: string;
  authAccountSystem: IM_API.Admin.AuthAccountSystem;

  tokopediaDomain: string;
  tokopediaStoreType: TokopediaStoreType;
  is3pf: boolean;
}

export interface GetAdminStoresParams {
  keyWord?: string;
  region?: General.UpperCaseRegion | 'WORLD';
  externalType?: General.PlatformUpperCase;
  storeType?: General.StoreType;
  page: number;
  pageSize?: number;
  order?: boolean;
  authStatus?: 1 | 2 | 3 | -1; // 1 已失效 2 即将失效
  departmentId?: string | number;
  permId?: General.PermissionId;
}

export const getAdminStores = (params: GetAdminStoresParams, signal?: AbortSignal) => {
  return storeHomeClient.get<
    ImResponse<{
      stores: AdminStore[];
      total: number;
    }>
  >('admin/stores', {
    params,
    signal,
  });
};

export interface AeAdminStore extends AdminStore {
  closedCsps: AeOverview.Csp[];
  usedCspCount: number;
  totalCspCount: number;
}

export const getAeAdminStores = (params: GetAdminStoresParams) => {
  return resolve(
    doorClient.get<ImResponse<{ infos: AeAdminStore[]; total: number }>>(
      '/aggregation/v1/getAggAeStoreSubscribeInfo',
      { params },
    ),
  );
};

export const getAdminStoresByIds = (storeIds: (number | string)[]) => {
  return resolve(
    storeHomeClient.post<ImResponse<{ stores: AdminStore[] }>>('/admin/stores_by_ids', {
      storeIds,
    }),
  );
};

export const getLastAuthInfo = (storeId: number | string) => {
  return resolve(
    storeHomeNVClient.get<
      ImResponse<{
        storeType?: 'LOCAL' | 'CROSS';
        departmentIds?: number[];
      }>
    >('/auth/last_auth_info', { params: { storeId } }),
  );
};

export type ImportAdminStoresBody =
  | {
      type: 'storeName';
      storeName: string[];
      departmentId?: number;
      permId?: General.PermissionId;
      platform?: General.PlatformUpperCaseTunnel;
      region?: General.UpperCaseRegion;
    }
  | {
      type: 'alias';
      alias: string[];
      departmentId?: number;
      permId?: General.PermissionId;
      platform?: General.PlatformUpperCaseTunnel;
      region?: General.UpperCaseRegion;
    }
  | {
      type: 'externalId';
      externalId: string[];
      departmentId?: number;
      permId?: General.PermissionId;
      platform?: General.PlatformUpperCaseTunnel;
      region?: General.UpperCaseRegion;
    };

export const importAdminStores = (body: ImportAdminStoresBody) => {
  return resolve(
    storeHomeV2Client.post<ImResponse<AdminStore[]>>('/admin/batch_query_stores', body),
  );
};

/** 商品知识模块店铺展示用：一次批量查询跨平台/站点的店铺（对齐 perseus batchQueryStoresList） */
export const batchQueryStoresList = (body: {
  batchQueryStoreList: Array<{
    externalType: string;
    region: string;
    externalId: string;
  }>;
}) => {
  return resolve(
    storeHomeV2Client.post<ImResponse<AdminStore[]>>('/admin/batch_query_stores_list', body),
  );
};

// 授权店铺需要协助通知
export const notifyAuthShopNeedHelp = () => {
  return storeHomeClient.post<ImResponse<undefined>>('admin/auth/notify_need_help', {});
};

export interface GenerateShopeeAuthUrlBody {
  authUrl: string;
  authUrlV2?: string;
  timestamp: number;
  redirectSign: string;
}

export const generateShopeeAuthUrl = (
  partnerType: IM_API.Admin.PartnerType,
  storeType: IM_API.Admin.StoreType,
  region: General.UpperCaseRegion,
) => {
  return storeHomeClient.post<ImResponse<GenerateShopeeAuthUrlBody>>('admin/shopee_auth_url', {
    partnerType,
    storeType,
    region,
  });
};

export interface GenerateStandAloneShopeeAuthUrlBody {
  url: string;
}

export const generateStandAloneShopeeAuthUrl = (
  partnerType: IM_API.Admin.PartnerType,
  storeType: IM_API.Admin.StoreType,
  region: General.UpperCaseRegion | 'CNSC',
  departmentIds: number[],
  uuid: string,
  authWay: ShopeeAuthWay,
  openMode?: AuthShopOpenMode,
  storehouseType?: StorehouseType,
) => {
  return storeHomeClient.post<ImResponse<GenerateStandAloneShopeeAuthUrlBody>>(
    'admin/shopee_auth/standalone_url',
    {
      partnerType,
      storeType,
      region,
      departmentIds,
      uuid,
      authWay,
      openMode,
      storehouseType,
    },
  );
};

export interface StandaloneAuthResult {
  uuid: string;
  isSuccess: boolean;
  errorMsg: string;
  /** 本次新增的店铺 */
  storeIds: number[];
  storeNames: string[];
  /** 全部授权店铺；storeIds 只含新增，统计须用它，旧后端缺省时退回 storeNames */
  allStoreIds?: number[];
}

export interface StandaloneAuthResultBody {
  erpResult: StandaloneAuthResult;
  chatResult: StandaloneAuthResult;
  /** 新版订阅价区对本次授权的裁剪结果；旧套餐/旧缓存可缺省 */
  authorizationScope?: AuthorizationScope;
}

export const getStandaloneAuthResult = (uuid: string) => {
  return storeHomeClient.get<ImResponse<StandaloneAuthResultBody>>(
    `admin/shopee_auth/standalone_result/${uuid}`,
  );
};

export interface PostShopeeAuthBody {
  partnerType: IM_API.Admin.PartnerType;
  storeType: IM_API.Admin.StoreType;
  region: General.UpperCaseRegion;
  timestamp: GenerateShopeeAuthUrlBody['timestamp'];
  redirectSign: GenerateShopeeAuthUrlBody['redirectSign'];
  code?: string;
  shopId?: number;
  mainAccountId?: number;
}

export const postShopeeAuth = (body: PostShopeeAuthBody) => {
  return storeHomeClient.post<ImResponse<string>>('admin/shopee_auth', body);
};

// 删除店铺
export const removeShopStore = (body: { shopId: number }) => {
  return storeHomeClient.post<ImResponse<string>>('admin/remove_shopee_store', body);
};

export interface LazadaAuthParams {
  code: string;
}

export const lazadaAuth = (params: LazadaAuthParams) => {
  return storeHomeClient.get('/admin/lazada/authResult/redirect', {
    params,
  });
};

export interface LazadaStandaloneAuthResultBody {
  isSuccess: boolean;
  successCount?: number;
  errorMsg: string;
  /** 新版订阅价区对本次授权的裁剪结果；旧套餐/旧缓存可缺省 */
  authorizationScope?: AuthorizationScope;
}

/** 当前付费价区未覆盖、因此不会接入系统的店铺 */
export interface ExcludedAuthorizationStore {
  storeName: string;
  externalStoreId?: string | null;
  platform?: string | null;
  region?: string | null;
}

/** 地区套餐对本次授权的裁剪结果 */
export interface AuthorizationScope {
  priceZone: string;
  authorizedRegions: string[];
  excludedStores: ExcludedAuthorizationStore[];
}

/** 授权前套餐范围（新版订阅价区）；authorizedRegions 为 null/缺省表示不限站点 */
export interface StoreAuthorizationPolicy {
  priceZone: string;
  authorizedRegions: string[] | null;
  exemptPlatforms?: string[];
}

export const getStoreAuthorizationPolicy = () => {
  return resolve(storeHomeNVClient.get<ImResponse<StoreAuthorizationPolicy>>('/auth/scope'));
};

export const getLazadaStandaloneAuthResult = (uuid: string) => {
  return storeHomeClient.get<ImResponse<LazadaStandaloneAuthResultBody>>(
    `admin/lazada/standalone_result/${uuid}`,
  );
};

export const getOrgSelf = () => {
  return storeHomeClient.get<
    ImResponse<{
      id: number;
      name: string;
      tryExpireTimestamp: number;
      orgChannel: 'SELF';
      externalOrgId: string;
    }>
  >('admin/org/self');
};

export const updateStoreAlias = (storeId: number | string, alias: string) => {
  return storeHomeClient.put(`admin/stores/${storeId}/alias`, { alias });
};

export const updateShopeeCredentials = (
  account: string,
  password: string,
  extra: {
    shopId?: number;
    mainAccountId?: number;
  },
) => {
  return storeHomeClient.post('admin/shopee/credentials', {
    account,
    password,
    ...extra,
  });
};

export interface AccountInfo {
  account: string;
  password: string;
  /** 0-普通店铺帐号，1-cnsc帐号 */
  accountType: number;
  region: string;
  storeId: number;
}

export const getShopeeCredential = (externalId: number) => {
  return resolve(
    storeHomeV2Client.get<ImResponse<AccountInfo>>('/admin/get_storeCredential_by_shopId', {
      params: { externalId },
    }),
  );
};

export interface UpdateCredentialReq {
  account: string;
  password: string;
  // 外部店铺id
  externalId: string;
  // 登录shopee后台成功以后标记为1，登录失败以后标记为0.默认值是0
  status?: 0 | 1;
}

export const updateCredential = (body: UpdateCredentialReq) => {
  return storeHomeV2Client.post<ImResponse<any>>('/admin/update_account_info', body);
};

// 帐号类型
export enum AccountType {
  // 普通帐号
  Normal = 1,
  // CNSC 帐号
  CNSC,
  // 子母帐号
  MainAndSub,
}

export interface batchUpdateShopeeCredentialsBody {
  orgId: number;
  authType?: number;
  credentialVOS: Array<{
    account: string;
    password: string;
    shopId: number;
    mainAccountId?: number;
    accountType: AccountType;
  }>;
}

// 批量更新店铺帐号密码
export const batchUpdateShopeeCredentials = (body: batchUpdateShopeeCredentialsBody) => {
  return storeHomeClient.post<ImResponse<any>>('admin/shopee/batch_credentials', {
    ...body,
    authType: body?.authType || 1,
  });
};

export const shopeeErrorList = () => {
  return resolve(storeHomeClient.get<ImResponse<Auth.ShopeeError[]>>('admin/shopee/auth_errors'));
};

export interface CancelShopeeUrlBody {
  shopeeAuthErrorId: number;
}

export type CancelShopeeUrlResponse = ImResponse<{
  authUrl: string;
  timestamp: number;
  redirectSign: string;
}>;

export const cancelShopeeUrl = (body: CancelShopeeUrlBody) => {
  return resolve(
    storeHomeClient.post<CancelShopeeUrlResponse>('admin/cancel_shopee_auth_url', body),
  );
};

export type CancelShopeeBaseBody<T> = {
  shopeeAuthErrorId: number;
  timestamp: number;
  redirectSign: string;
} & T;

export type CancelShopeeBody =
  | CancelShopeeBaseBody<{
      shopId: number;
    }>
  | CancelShopeeBaseBody<{
      mainAccountId: number;
    }>;

export const cancelShopee = (body: CancelShopeeBody) => {
  return resolve(storeHomeClient.post<ImResponse<never>>('admin/cancel_shopee_auth', body));
};

export const generateLazadaAuthUrl = (
  uuid?: string,
  authWay?: PlatformShopAuthWayType,
  departments?: number[],
  openMode?: AuthShopOpenMode,
) => {
  return storeHomeClient.get<
    ImResponse<{ authUrl: string; timestamp: number; redirectSign: string }>
  >('admin/lazada/authUrl/redirect', {
    params: { uuid, authWay, departmentIds: departments?.join(','), openMode },
  });
};

export function getUserConfig(signal?: AbortSignal) {
  return resolve(
    storeHomeClient.get<
      ImResponse<{
        qaAutoTranslate: boolean;
        isFirstLogin: boolean;
        receptionConfig: General.ReceptionConfig;
      }>
    >('admin/user/config', { signal }),
  );
}

export function updateUserConfig(body: General.ReceptionConfig) {
  return storeHomeClient.post('/admin/user/config/update', body);
}

export function updateQaAutoTranslateConfig(qaAutoTranslate: boolean) {
  return resolve(
    storeHomeClient.put<ImResponse<{ qaAutoTranslate: boolean }>>(
      'admin/user/config/qaAutoTranslate',
      {
        qaAutoTranslate,
      },
    ),
  );
}

export function transferedStoreList() {
  return resolve(storeHomeClient.get<ImResponse<number[]>>('/stores/transferred'));
}

export function orgConfig() {
  return resolve(
    doorClient.get<ImResponse<Pick<General.OrgConfig, 'remindEnable'>>>(
      '/overseaim-store-home/v1/org-config/orgConfig',
    ),
  );
}

export function enterprise() {
  return resolve(
    storeHomeNVClient.get<ImResponse<Oversea.Enterprise | undefined>>(
      '/certification/get_certification_info',
    ),
  );
}

export interface UpdateEnterpriseBody extends Oversea.Enterprise {}

export function updateEnterprise(body: UpdateEnterpriseBody) {
  return resolve(
    storeHomeNVClient.post<ImResponse<undefined>>('/certification/update_certification', body),
  );
}

export function org() {
  return resolve(storeHomeClient.get<ImResponse<Oversea.Org>>('/admin/org/self'));
}

export const checkIsPhoneRegistered = (phone: string) => {
  return resolve(
    loginClientV2.get<ImResponse<{ registered: boolean }>>('/admin/check_registered', {
      params: { phone },
    }),
  );
};

export const updateOgInfo = (body: { name: string }) => {
  return storeHomeClient.put<ImResponse<any>>('/admin/org_info', body);
};

export const verifySMS = (body: { phone: string; smsCode: string }) => {
  return loginClientV2.post<ImResponse<{ isMatch: boolean }>>('/admin/verify_sms_code', body);
};

export function exportStoreList(params: Omit<GetAdminStoresParams, 'page' | 'pageSize'>) {
  return resolve(
    storeHomeV2Client.post<ImResponse<string>>('/admin/export_store_info', { ...params }),
  );
}

export function batchUpdateStoreAlias(file: FormData) {
  return storeHomeV2Client.post<ImResponse<void>>('/admin/batch_update_storeAlias', file);
}

export interface NeedToUpdateStore {
  storeId: string;
  account: string;
  password: string;
  storeName: string;
  storeAlias: string;
  region: General.UpperCaseRegion;
}

export function getInvalidAccountNum() {
  return resolve(
    storeHomeV2Client.get<ImResponse<NeedToUpdateStore[]>>('/admin/get_invalid_account'),
  );
}

export function batchUpdateAccount(
  accounts: Pick<NeedToUpdateStore, 'account' | 'storeId' | 'password'>[],
) {
  return resolve(
    storeHomeV2Client.post<ImResponse<undefined>>('/admin/batch_update_credential', {
      batchUpdateCredentials: accounts,
    }),
  );
}

export function getUnassignedStore() {
  return resolve(
    storeHomeNVClient.get<ImResponse<Store.Store[]>>('/assistant_group/get_unassigned_store'),
  );
}

interface Store2GroupParams {
  storeIds: number[];
  assistantGroupIds: number[];
}

export function distributeStore2Group(params: Store2GroupParams) {
  return resolve(
    storeHomeNVClient.post<ImResponse<undefined>>('/assistant_group/store_distribute', {
      ...params,
    }),
  );
}

type AuthExceptionNumKey =
  | `${
      | Exclude<General.Platform, 'aliexpress' | 'tiktok_us' | 'facebook'>
      | 'tiktokUs'
      | 'aliExpress'}AuthExceptionNum`;

type AuthBeAboutExpireNumKey =
  | `${
      | Exclude<General.Platform, 'aliexpress' | 'tiktok_us' | 'facebook'>
      | 'tiktokUs'
      | 'aliExpress'}AuthBeAboutExpireNum`;

type LocalRegisterExceptionNumKey = `${'shopee'}LocalRegisterExceptionNum`;

export interface StoreAuthErrorCount
  extends Record<
    | AuthExceptionNumKey
    | AuthBeAboutExpireNumKey
    | LocalRegisterExceptionNumKey
    | 'tiktokPermissionExceptionNum'
    | 'tiktokNoCsatPermissionNum'
    | 'tiktokUsPermissionExceptionNum'
    | 'tiktokUsNoCsatPermissionNum'
    | 'tokopediaMigrateTiktokNotAuthNum',
    number
  > {
  // lazada 授权异常店铺
  lazadaAuthExceptionNum: number;
  // shopee 授权异常店铺
  shopeeAuthExceptionNum: number;
  // tokopediaAuthExceptionNum: number;
  // tiktokAuthExceptionNum: number;
  // shopee 未完成注册店铺
  // shopeeLocalRegisterExceptionNum: number;
  // [KK extends K]: number
}

// 获取店铺授权失败数
export function getStoreAuthErrorCount(orgId: number, signal?: AbortSignal, isPerformance = false) {
  return storeHomeClient.get<ImResponse<StoreAuthErrorCount>>('admin/getStoreAuthExceptionNum', {
    params: { orgId },
    signal,
    headers: isPerformance
      ? {
          'x-scene': 'STORE_PERFORMANCE',
        }
      : undefined,
  });
}

export interface ConfigOverviewParams {
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
}

export interface ConfigOverviewResponse {
  configItems: Aide.Scenario[];
}

export function configOverview(params: ConfigOverviewParams) {
  return resolve(
    storeHomeV2Client.get<ImResponse<ConfigOverviewResponse>>('admin/config_overview', { params }),
  );
}

export interface StoreUsersParams {
  storeId: number | string;
  permId: General.PermissionId;
}

export function storeUsers(params: StoreUsersParams) {
  return resolve(
    storeHomeV2Client.get<ImResponse<Assistant.Account[]>>('/admin/query_user_by_stores', {
      params,
    }),
  );
}

export function getAliExpressAuthUrl() {
  return resolve(storeHomeNVClient.get<ImResponse<string>>('/aliexpress/generateAuthUrl'));
}

// 批量删除店铺
export function deleteStores(body: { storeIds: number[] }) {
  return resolve(storeHomeV2Client.post<ImResponse<undefined>>('/admin/del_store', body));
}

interface ConfigKeyResponseMap {
  NON_RECEPTION_PROCESS_DAILY_LIMIT: number;
  NON_RECEPTION_PROCESS_MONTHLY_LIMIT: number;
  // 查询AI服务买家配置
  AI_SERVICE_BUYER_LIMIT: number;
}

export type ConfigKey = keyof ConfigKeyResponseMap;

export function queryConfigByKey<T extends ConfigKey>(configKey: T) {
  return resolve(
    storeHomeV2Client.get<ImResponse<ConfigKeyResponseMap[T]>>('/admin/query_config_by_key', {
      params: { configKey },
    }),
  );
}

export function getBotUsageSummary() {
  return resolve(
    doorClient.get<
      ImResponse<{
        package: number;
        sendLimit: number;
        sentNum: number;
        endTime: string;
        abandonedNum: number;
      }>
    >('/aggregation/v1/getBotUsageSummary'),
  );
}

export function whiteList() {
  return resolve(
    storeHomeV2Client.get<ImResponse<General.WhiteListConfig>>('admin/list_white_list'),
  ).then(({ AIDE_REMOVE_TASK, SHIPPING_AND_LOGISTICS, BOOTGUIDE, ...others }) => {
    return {
      ...others,
      BOOTGUIDE,
      AIDE_REMOVE_TASK: AIDE_REMOVE_TASK || BOOTGUIDE, // 开启配置引导时，需要开启去除任务层级
      SHIPPING_AND_LOGISTICS: SHIPPING_AND_LOGISTICS || BOOTGUIDE,
    };
  });
}

// 查询所有挂起子状态
export const getUserHangupSubStatus = () => {
  return resolve(storeHomeV2Client.get<ImResponse<string[]>>('admin/user_statue'));
};

export interface PostUserHangupSubStatusData {
  originalState: string;
  newState: string;
}

// 创建或者更新挂起子状态
export const postUserHangupSubStatus = (data: PostUserHangupSubStatusData) => {
  return resolve(storeHomeV2Client.post<ImResponse<undefined>>('admin/user_statue', data));
};

export interface DeleteUserHangupSubStatusData {
  originalState: string;
}

// 删除挂起子状态
export const deleteUserHangupSubStatus = (data: DeleteUserHangupSubStatusData) => {
  return resolve(storeHomeV2Client.post<ImResponse<undefined>>('admin/del_user_state', data));
};

export interface SortUserHangupSubStatusData {
  states: string[];
}
// 挂起子状态排序
export const sortUserHangupSubStatus = (data: SortUserHangupSubStatusData) => {
  return resolve(storeHomeV2Client.post<ImResponse<undefined>>('admin/sort_user_state', data));
};

// 获取店铺不同状态的数量
export const getStoreStatusCount = (
  externalType: General.PlatformUpperCaseTunnel,
  region: General.UpperCaseRegion,
) => {
  return resolve(
    storeHomeV2Client.get<
      ImResponse<{
        totalCount: number;
        beAboutToExpireCount: number;
        expiredCount: number;
      }>
    >('admin/store_states_count', {
      params: { externalType, region },
    }),
  );
};

export interface AssistantFromSameDeptData {
  account: string;
  email: string;
  id: number;
  name: string;
  orgId: number;
  productLine: string;
  sleepFlag: boolean;
  status: string;
  type: number;
}

// 查询同部门下的其他客服
export const getAssistantFromSameDept = (signal?: AbortSignal) => {
  return resolve(
    storeHomeV2Client.get<ImResponse<AssistantFromSameDeptData[]>>(
      'admin/get_assistant_from_same_dept',
      { signal },
    ),
  );
};

export interface ExportCount {
  inProgressCount: number;
  successCount: number;
  failCount: number;
  total: number;
  lastDownloadCreateTimeStamp: number;
}

export const getExportCount = () => {
  return resolve(storeHomeV2Client.get<ImResponse<ExportCount>>('admin/export_count'));
};

export interface QueryExportLogParams {
  page?: number;
  pageSize?: number;
}

export enum ExportLogStatus {
  inProgress = 1,
  succeed = 2,
  failed = 3,
}
export interface ExportLog {
  id: number;
  fileName: string;
  // 1 是进行中 2成功 3失败
  status: ExportLogStatus;
  url: string;
  createTimeStamp: number;
  errorMsg?: string;
}
export const getExportList = (params: QueryExportLogParams) => {
  return storeHomeV2Client.get<ImResponse<ExportLog[]> & { count: number }>(
    'admin/list_export_log',
    { params },
  );
};

export const retryExport = (id: number) => {
  return storeHomeV2Client.post('admin/retry_export', { id });
};

// 查询 org 是否有店铺
export const getIsExistStore = () => {
  return resolve(storeHomeV2Client.get<ImResponse<boolean>>('admin/is_exist_store'));
};

// 生成 mercado 授权链接
export const generateMercadoAuthUrl = (
  storeType: IM_API.Admin.StoreType,
  departmentIds: number[],
  uuid: string,
  region?: General.UpperCaseRegion,
) => {
  return resolve(
    storeHomeNVClient.get<ImResponse<string>>('mercado/generateAuthUrl', {
      params: {
        storeType,
        departmentIds,
        region,
        uuid,
      },
    }),
  );
};

export const mercadoStandaloneResult = (uuid: string) => {
  return resolve(
    storeHomeNVClient.get<ImResponse<LazadaStandaloneAuthResultBody>>('mercado/standalone_result', {
      params: {
        uuid,
      },
    }),
  );
};

export const generateAuthUrl = (params: {
  externalType: General.PlatformUpperCase;
  storeType: IM_API.Admin.StoreType;
  departmentIds: number[];
  region?: General.UpperCaseRegion;
}) => {
  return resolve(storeHomeNVClient.get<ImResponse<string>>('/auth/generateAuthUrl', { params }));
};

export const standaloneResult = (uuid: string) => {
  return resolve(
    storeHomeNVClient.get<ImResponse<LazadaStandaloneAuthResultBody>>('/auth/standalone_result', {
      params: { uuid },
    }),
  );
};

export const syncStoreInfo = (data: {
  externalType: General.PlatformUpperCaseTunnel;
  region: General.UpperCaseRegion;
}) => {
  return resolve(storeHomeV2Client.post<ImResponse<undefined>>('admin/sync_store_info', data));
};

export const getOrgRegions = () => {
  return resolve(
    storeHomeV2Client.get<
      ImResponse<{
        regions: General.UpperCaseRegion[];
      }>
    >('/admin/org_region'),
  );
};

export const getRegionLanguageMap = () => {
  return resolve(
    storeHomeV2Client.get<
      ImResponse<{
        translationConfig?: Partial<Record<General.UpperCaseRegion | 'FACEBOOK', General.Language>>;
      }>
    >('/admin/get_region_default_language'),
  );
};

export const updateRegionLanguageMap = (data: {
  translationConfig: Partial<Record<General.UpperCaseRegion | 'FACEBOOK', General.Language>>;
}) => {
  return storeHomeV2Client.post('/admin/update_region_default_language', data);
};

export const getPlatformRegions = () => {
  return resolve(
    storeHomeV2Client.get<
      ImResponse<{
        platformRegions: Record<General.PlatformUpperCase, General.UpperCaseRegion[]>;
      }>
    >('/admin/platform/regions'),
  );
};

export interface Notice {
  id: number;
  name: string;
  type: 'CONFIG_BOOTSTRAP';
  multiLanguageMap: Record<General.SystemLanguage, 'string'>;
}

export const getNoticeList = () => {
  return resolve(
    storeHomeNVClient.get<
      ImResponse<{
        advertisementList: Notice[];
      }>
    >('/notice/ad'),
  );
};

export const readNotice = (id: number | string) => {
  return storeHomeNVClient.post(`/notice/ad/read/${id}`, null);
};

export const getLightWeightPackageInfo = (signal?: AbortSignal) => {
  return resolve<{
    data: Pay.OrderedLightWeightPkg[];
    usedOrderRate: number;
  }>(storeHomeV2Client.get('/admin/get_light_weight_package_info', { signal }));
};

type OrgConfigNew = {
  enableAI: boolean;
};

export const getOrgConfig = (signal?: AbortSignal) => {
  return resolve<OrgConfigNew>(storeHomeV2Client.get('/admin/org/config', { signal }));
};

export const updateOrgConfig = (data: OrgConfigNew) => {
  return storeHomeV2Client.post('/admin/org/config', data);
};

export const loginByAuthCode = (data: {
  code: string;
  purpose: 'register' | 'verify';
  channel?: string;
  type: 'GOOGLE' | 'FACEBOOK';
  redirectUrl: string;
  authType: 'code' | 'accessToken' | 'jwt';
}) => {
  return resolve<{
    type: 'FIRST_REGISTRATION' | 'FIRST_REGISTRATION_EXIST_SAME_EMAIL' | 'ALREADY_EXIST_USER';
    token?: string;
    uuid?: string;
    email?: string;
  }>(storeHomeClient.post('/login_by_auth_code', data));
};

export const loginByExternal = (data: { uuid: string; channel?: string }) => {
  return resolve<{
    token: string;
  }>(storeHomeClient.post('/login_by_external', data));
};

export type ExternalLoginCheckResponse = {
  account: string;
  type: 'SUCCESS' | 'ONLY_CAN_EXTERNAL_LOGIN' | 'EXTERNAL_LOGIN_AND_OTHER_LOGIN';
  uuid: string;
};
export const externalLoginCheck = (data: {
  code: string;
  type: 'GOOGLE' | 'FACEBOOK';
  redirectUrl: string;
  authType: 'code' | 'accessToken' | 'jwt';
}) => {
  return resolve<ExternalLoginCheckResponse>(
    storeHomeV2Client.post('/admin/external_login/check', data),
  );
};

export const externalLoginBind = (data: { uuid: string }) => {
  return storeHomeV2Client.post('/admin/external_login/bind', data);
};

export const externalLoginUnbind = (data: { type: 'GOOGLE' | 'FACEBOOK' }) => {
  return storeHomeV2Client.post('/admin/external_login/un_bind', data);
};

export interface VerifyCodeV2Data {
  phoneNumber: string;
  purpose: 'verify' | 'register';
  captchaText?: string;
}

export const sendVerifyCodeV2 = (data: VerifyCodeV2Data) => {
  return storeHomeClient.post<
    ImResponse<{
      captchaImage: string;
    }>
  >('/admin/verification/codes/v2', data);
};

export const getUserByPermission = (scene: SearchStoreListScene, signal?: AbortSignal) => {
  return resolve(
    storeHomeV2Client.get<
      ImResponse<
        Pick<Assistant.Account, 'id' | 'account' | 'name' | 'status' | 'sleepFlag' | 'email'>[]
      >
    >('admin/get_user_by_perm', { params: { scene }, signal }),
  );
};

export const getListUserByPermission = (scene: 'PRODUCT_KNOWLEDGE', signal?: AbortSignal) => {
  return resolve(
    storeHomeV2Client.get<
      ImResponse<
        Pick<Assistant.Account, 'id' | 'account' | 'name' | 'status' | 'sleepFlag' | 'email'>[]
      >
    >('admin/list_user_by_perm', { params: { scene }, signal }),
  );
};
